
import * as R from 'ramda';
import * as Sentry from '@sentry/browser';

import Events from '~/events';
import * as stores from './stores';
import * as D from '~/data-utils';

import InhouseFacilitiesProvider from '~/service/facilities/inhouse';

import Header from 'components/header/Header.svelte';
import RightSidebar from 'components/RightSidebar.svelte';
import Banner from 'components/Banner.svelte';
import App from './App.svelte';
import PlayerView from 'components/player/PlayerView.svelte';


const getTarget = select => {
  const target = document.querySelector(select);
  if (!target) throw new Error(`Can't find target '${select}'`);
  return target;
};

export default class IMDApp {

  constructor () {
    const [, hash] = window.location.pathname.split('/');
    this.context = null;
    this.hash = hash;

    stores.hash.set(hash);
    stores.viewActive.set(location.hash);
  }

  setContext (context) {

    if (this.context) return;

    this.context = context;
    
    const {
      playerTimeout,
      sessionTimeout
    } = stores.setupTimeouts(context.imdSettings);

    playerTimeout.subscribe(value => {
      stores.playerOn.set(value && !this.encounteredFatalError);
    });

    this.playerTimeout = playerTimeout;
    this.sessionTimeout = sessionTimeout;

    Events.addEventListener(
      'data:updated',
      _ => this.dataUpdated()
    );

    Events.addEventListener(
      'user:activity',
      _ => {
        stores.userLastActive.set(new Date());
        stores.screensaverOn.set(false);
      }
    );

    Events.addEventListener(
      'screensaver:start',
      _ => stores.screensaverOn.set(true)
    );

    Events.addEventListener(
      '3rd-party:open',
      ({ detail }) => stores.selected3rdParty.set(detail)
    );

    Events.addEventListener(
      'view:change',
      e => {
        const [view, prev] = stores.viewActive.set(e.detail);

        if (view == 'start' || (view == 'search' && prev == 'maps')) {
          this.resetSearch();
        }

        document.title = `${view} · ${this.hash} · HyperIn DS`;

        Sentry.addBreadcrumb({
          category: 'x-navigation',
          data: {
            from: `#${prev}`,
            to: `#${view}`
          }
        });
      }
    );

    Events.addEventListener(
      'language:change',
      e => stores.lang.set(e.detail)
    );

    Events.addEventListener(
      'error:fatal',
      () => {
        this.encounteredFatalError = true;
        stores.playerOn.set(false);
      }
    );

    Events.addEventListener(
      'ui:transform',
      ({ detail }) => stores.selected3rdParty.set(detail)
    );

    // INIT components
    [
      [Header, '#header', true],
      [RightSidebar, '#page-container', true],
      [Banner, '#footer'],
      [App, '#view .view__svelte', true],
      [PlayerView, 'body', true]
    ]
      .forEach(([comp, selector, passApp]) => {
        try {
          new comp({
            target: getTarget(selector),
            props: passApp ? {
              app: this
            } : {}
          });
        } catch (e) {
          console.log('Failed to init', comp);
          console.error(e);
        }
      });
  }

  resetSearch () {
    stores.searchTerm.set('');
  }

  feature (key, _default) {
    return this.setting(`feature.${key}`, _default);
  }

  setting (key, _default) {
    return this.key(`imdSettings.${key}`, _default);
  }

  key (key, _default) {
    return R.pathOr(_default, key.split('.'), this.context);
  }

  service (name) {
    return name ? this.context.service[name] : this.context.service;
  }

  setResources (resources) {
    stores.resources.set(resources);
  }

  dataUpdated () {
    stores.lang.set(D.lang());
    stores.offers.set(D.getOffers());
    stores.openingHours.set(D.getOpeningHours());
    stores.stores.set(D.getStores());
    stores.newsAndEvent.set(D.getNewsAndEvents());
    stores.banners.set(D.getDataWithDefault('imd-banners', { key: 'imdBanners', lang: 'all' }));
    stores.mediaPlaylist.set(D.getDataWithDefault('media-playlist', { lang: 'all' }));

    const { mapType } = this.context.imdSettings;
    if (mapType.inhouse) {
      this.service('facilities').setProvider(
        new InhouseFacilitiesProvider()
      );
    }
  }
}
